import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import Table from '@risksmart-app/components/src/table';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import TabHeader from 'src/components/tab-header';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { Permission } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useDeleteAcceptances } from '@/hooks/mutations/acceptance';
import { useGetAcceptancesByParentRiskId } from '@/hooks/queries';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';

import { useGetCollectionStatelessTableProps } from '../../../../acceptances/config';
import type { AcceptanceTableFields } from '../../../../acceptances/types';

interface Props {
  parent: ObjectWithContributors;
}

const Tab: FC<Props> = ({ parent }) => {
  useI18NSummaryHelpContent('acceptances.help');
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'acceptances',
  });
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const navigate = useNavigate();
  const [selectedAcceptances, setSelectedAcceptances] = useState<
    AcceptanceTableFields[]
  >([]);

  const {
    hasPermission: canDeleteAcceptances,
    loading: canDeleteAcceptancesLoading,
  } = useHasPermissionQuery('delete:acceptance', parent);
  const riskId = useGetGuidParam('riskId');
  const { data, loading, refetch } = useGetAcceptancesByParentRiskId({
    queryArgs: { riskId },
  });

  const { deleteAcceptances, loading: deleteLoading } = useDeleteAcceptances();

  const tableProps = useGetCollectionStatelessTableProps(data?.acceptance, {
    sortingColumn: 'ModifiedAtTimestamp',
    sortingDirection: 'desc',
  });

  // An acceptance with a deletion change request already in flight cannot take a
  // second one — the approval gate rejects it with "You cannot edit this object
  // while a change request is in progress", which reads here as an unexplained
  // failure (COI-916). Those rows are unselectable below; this is the matching
  // guard on submit.
  //
  // The flag is resolved from the CURRENT rows by id, not from the selection
  // itself: `selectedAcceptances` holds the row objects as they were when the
  // user ticked them, so a refetch that turns one into a pending deletion never
  // updates those copies.
  const deletableSelection = useMemo(() => {
    const pendingDeleteIds = new Set(
      (tableProps.allItems ?? [])
        .filter((a) => a.hasPendingDeleteRequest)
        .map((a) => a.Id)
    );

    return selectedAcceptances.filter((a) => !pendingDeleteIds.has(a.Id));
  }, [selectedAcceptances, tableProps.allItems]);

  const handleAcceptanceOpen = () => {
    navigate('add');
  };

  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      await deleteAcceptances(deletableSelection.map((s) => s.Id));
      // Everything past the mutation is local cleanup and a view refresh, so it
      // settles the UI BEFORE the refetch. Previously a rejected refetch
      // propagated out of `asyncAction` and was reported as a failed delete —
      // wrong on both counts: the delete had succeeded, and `failureAction`
      // never ran, so the modal stayed open over a stale table.
      setSelectedAcceptances([]);
      setIsDeleteModalVisible(false);

      // The register read is `no-cache`, so this refetch is the only thing that
      // refreshes the tab (and it is what flips a just-requested deletion to a
      // disabled row). A failure here means the list is stale, not that the
      // delete failed — swallow it rather than mislabel the outcome. The next
      // navigation or mount re-reads anyway.
      try {
        await refetch();
      } catch {
        // The mutation already succeeded; a later navigation will refresh the list.
      }

      return true;
    },
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
  });

  return (
    <>
      <Table
        {...tableProps}
        selectionType={
          canDeleteAcceptances && !canDeleteAcceptancesLoading
            ? 'multi'
            : undefined
        }
        selectedItems={selectedAcceptances}
        isItemDisabled={(item) => item.hasPendingDeleteRequest}
        trackBy={'Id'}
        onSelectionChange={({ detail }) => {
          setSelectedAcceptances(detail.selectedItems);
        }}
        header={
          <SpaceBetween size={'m'}>
            <TabHeader
              actions={
                <SpaceBetween direction={'horizontal'} size={'xs'}>
                  <Permission
                    permission={'delete:acceptance'}
                    parentObject={parent}
                  >
                    <Button
                      formAction={'none'}
                      variant={'normal'}
                      disabled={!deletableSelection.length}
                      onClick={() => setIsDeleteModalVisible(true)}
                    >
                      {t('delete')}
                    </Button>
                  </Permission>
                  <Permission
                    permission={'insert:acceptance'}
                    parentObject={parent}
                  >
                    <Button
                      variant={'primary'}
                      formAction={'none'}
                      onClick={handleAcceptanceOpen}
                    >
                      {st('create_button')}
                    </Button>
                  </Permission>
                </SpaceBetween>
              }
            >
              {st('tab_title')}
            </TabHeader>
          </SpaceBetween>
        }
        variant={'embedded'}
        loading={loading}
      />
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={t('delete')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </>
  );
};

export default Tab;
